import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { Checkbox } from "@/components/ui/checkbox";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend,
} from "recharts";

const COLORS = ["#002FA7", "#00A859", "#F59E0B", "#B429A6", "#FF2A2A"];

export default function ComparePage() {
  const [tools, setTools] = useState([]);
  const [criteria, setCriteria] = useState([]);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/tools"), api.get("/criteria")]).then(([t, c]) => {
      setTools(t.data);
      setCriteria(c.data);
      setSelected(t.data.slice(0, 2).map((x) => x.id));
    });
  }, []);

  const toggle = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : prev.length >= 5 ? prev : [...prev, id]));
  };

  const chosen = tools.filter((t) => selected.includes(t.id));
  const radarData = criteria.map((c) => {
    const row = { criterio: c.name };
    chosen.forEach((t) => { row[t.name] = t.scores?.[c.id] ?? 0; });
    return row;
  });

  return (
    <Layout>
      <PageHeader title="Confronto tool" subtitle="Fino a 5 tool a confronto" />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-border p-4" data-testid="compare-selector">
          <div className="data-label mb-3">Seleziona tool</div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {tools.map((t) => (
              <label key={t.id} className="flex items-center gap-3 text-sm cursor-pointer py-1">
                <Checkbox checked={selected.includes(t.id)} onCheckedChange={() => toggle(t.id)} data-testid={`compare-check-${t.name}`} />
                <span className="flex-1">{t.name}</span>
                <span className="font-mono text-xs text-muted-foreground">{t.overall_score}</span>
              </label>
            ))}
            {tools.length === 0 && <p className="text-sm text-muted-foreground">Nessun tool disponibile.</p>}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-border p-6" data-testid="compare-radar">
          <h3 className="font-heading font-bold text-lg tracking-tight mb-4">Confronto profili</h3>
          {chosen.length === 0 ? (
            <p className="text-sm text-muted-foreground py-12 text-center">Seleziona almeno un tool.</p>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#E5E7EB" />
                <PolarAngleAxis dataKey="criterio" tick={{ fontSize: 12, fill: "#4B5563" }} />
                <PolarRadiusAxis domain={[0, 10]} tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#9CA3AF" />
                {chosen.map((t, i) => (
                  <Radar key={t.id} name={t.name} dataKey={t.name} stroke={COLORS[i]} fill={COLORS[i]} fillOpacity={0.12} strokeWidth={2} />
                ))}
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          )}
        </div>

        {chosen.length > 0 && (
          <div className="lg:col-span-4 bg-white border border-border overflow-x-auto" data-testid="compare-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="data-label px-4 py-3">Criterio</th>
                  {chosen.map((t) => <th key={t.id} className="data-label px-4 py-3 text-right">{t.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {criteria.map((c, idx) => (
                  <tr key={c.id} className={`border-b border-border ${idx % 2 ? "bg-[#F9FAFB]" : "bg-white"}`}>
                    <td className="px-4 py-2 font-medium">{c.name}</td>
                    {chosen.map((t) => <td key={t.id} className="px-4 py-2 text-right font-mono">{t.scores?.[c.id] ?? 0}</td>)}
                  </tr>
                ))}
                <tr className="border-t-2 border-primary bg-secondary font-semibold">
                  <td className="px-4 py-2">Punteggio complessivo</td>
                  {chosen.map((t) => <td key={t.id} className="px-4 py-2 text-right font-mono text-primary">{t.overall_score}</td>)}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
